import { ComponentType, lazy, LazyExoticComponent } from 'react';

const RELOAD_FLAG_KEY = 'lazy-reload-once';

interface LazyRetryOptions {
  /**
   * Title khi lỗi do chunk (dùng nếu sau khi reload mà vẫn lỗi)
   */
  chunkErrorTitle?: string;

  /**
   * Title khi lỗi không phải lỗi chunk (lỗi code trong component, v.v.)
   */
  otherErrorTitle?: string;
}

/**
 * Wraps a lazy import with automatic page reload on chunk-load failure.
 * - Nếu lỗi do dynamic import (chunk lỗi / file không tồn tại):
 *    → reload page (tối đa 1 lần / tab).
 * - Nếu vẫn lỗi hoặc lỗi loại khác:
 *    → throw Error có kèm title (để ErrorBoundary hiển thị).
 */
export function lazyRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  options?: LazyRetryOptions,
): LazyExoticComponent<T> {
  return lazy(async () => {
    const isChunkLoadError = (error: unknown): boolean => {
      const msg = (error as any)?.message ?? '';

      if (typeof msg !== 'string') return false;

      return (
        msg.includes('Failed to fetch dynamically imported module') ||
        msg.includes('Loading chunk') ||
        msg.includes('Loading CSS chunk') ||
        msg.includes('ChunkLoadError')
      );
    };

    try {
      const mod = await importFn();

      // Nếu load thành công thì clear flag reload (nếu có)
      if (typeof window !== 'undefined' && 'sessionStorage' in window) {
        try {
          window.sessionStorage.removeItem(RELOAD_FLAG_KEY);
        } catch {
          // ignore
        }
      }

      return mod;
    } catch (error) {
      // Case: lỗi do chunk (file js bị mất / version mismatch)
      if (typeof window !== 'undefined' && isChunkLoadError(error)) {
        try {
          const hasReloaded = window.sessionStorage.getItem(RELOAD_FLAG_KEY);

          // Chỉ cho reload 1 lần để tránh vòng lặp vô hạn
          if (!hasReloaded) {
            window.sessionStorage.setItem(RELOAD_FLAG_KEY, '1');
            window.location.reload();
            // Trả về Promise never resolve để React không render tiếp
            return new Promise<never>(() => {});
          }
        } catch {
          // Nếu sessionStorage lỗi (Safari private mode...) thì cứ reload
          window.location.reload();
          return new Promise<never>(() => {});
        }

        // Nếu tới đây là: đã reload 1 lần rồi mà vẫn lỗi chunk
        const originalMessage = (error as any)?.message ?? String(error ?? 'Unknown error');
        const title =
          options?.chunkErrorTitle ?? options?.otherErrorTitle ?? 'Lỗi tải tài nguyên (chunk)';

        const wrapped = new Error(`${title}: ${originalMessage}`);
        (wrapped as any).cause = error;
        throw wrapped;
      }

      // Case: lỗi khác (bug trong component, lỗi runtime...)
      const originalMessage = (error as any)?.message ?? String(error ?? 'Unknown error');
      const title = options?.otherErrorTitle ?? 'Lỗi tải component (lazy import)';

      const wrapped = new Error(`${title}: ${originalMessage}`);
      (wrapped as any).cause = error;

      // Cho ErrorBoundary / React biết lỗi có title rõ ràng
      throw wrapped;
    }
  });
}
